import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, ArrowUpRight, Github } from "lucide-react";
import { profile } from "@/data/portfolio";
import { scrollToId } from "@/hooks/usePortfolio";
import { TID } from "@/constants/testIds";
const EASE = [0.16, 1, 0.3, 1];

// Each character assembles independently with spring physics + blur→sharp + overshoot.
function AssembleWord({ text, className, base = 0, accent = false }) {
  const reduce = useReducedMotion();
  return (
    <span className={`inline-block ${accent ? "signal" : ""} ${className || ""}`}>
      {text.split("").map((ch, i) => (
        <motion.span
          key={i}
          className="inline-block"
          initial={reduce ? { opacity: 1 } : { opacity: 0, y: 40, filter: "blur(10px)", scale: 0.85 }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)", scale: 1 }}
          transition={{
            delay: base + i * 0.035,
            type: "spring",
            stiffness: 220,
            damping: 14,
            filter: { type: "tween", duration: 0.55, ease: "easeOut", delay: base + i * 0.035 },
          }}
        >
          {ch === " " ? "\u00A0" : ch}
        </motion.span>
      ))}
    </span>
  );
}

export function Hero() {
  const ref = useRef(null);
  const reduce = useReducedMotion();

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yText = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -80]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, reduce ? 1 : 1.08]);
  const opacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  // Mouse-reactive lighting
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const lx = useSpring(mx, { stiffness: 60, damping: 20 });
  const ly = useSpring(my, { stiffness: 60, damping: 20 });
  const orbX = useTransform(lx, [0, 1], [-40, 40]);
  const orbY = useTransform(ly, [0, 1], [-30, 30]);
  const lightBg = useTransform(
    [lx, ly],
    ([x, y]) => `radial-gradient(120% 120% at ${x * 100}% ${y * 100}%, hsl(var(--primary) / 0.10), transparent 60%)`
  );

  const onMove = (e) => {
    if (reduce) return;
    mx.set(e.clientX / window.innerWidth);
    my.set(e.clientY / window.innerHeight);
  };

  return (
    <section
      id="hero"
      ref={ref}
      data-testid={TID.hero}
      onMouseMove={onMove}
      className="relative flex min-h-[88svh] items-center overflow-hidden md:min-h-[100svh]"
    >
      {/* Atmosphere: breathing gradient orbs + mouse light */}
      <motion.div className="pointer-events-none absolute inset-0" style={{ background: reduce ? undefined : lightBg }}>
        <motion.div className="orb orb-a h-[38vw] w-[38vw] bg-primary/15" style={{ left: "8%", top: "18%", x: reduce ? 0 : orbX, y: reduce ? 0 : orbY }} />
        <motion.div className="orb orb-b h-[30vw] w-[30vw] bg-[hsl(190_80%_55%/0.12)]" style={{ right: "10%", bottom: "12%", x: reduce ? 0 : orbY, y: reduce ? 0 : orbX }} />
      </motion.div>

      <motion.div
        style={{ y: yText, scale, opacity }}
        className="relative z-10 mx-auto w-full max-w-6xl px-5 sm:px-8"
      >
        <motion.div
          data-testid={TID.heroStatus}
          initial={reduce ? { opacity: 1 } : { opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.1 }}
          className="mb-8 inline-flex items-center gap-2.5 rounded-full border border-primary/30 bg-primary/5 px-3.5 py-1.5 backdrop-blur-sm"
        >
          <span className="h-2 w-2 rounded-full bg-primary pulse-dot" />
          <span className="font-mono text-[0.72rem] tracking-wide text-primary">{profile.status}</span>
        </motion.div>

        <h1 className="font-display text-[clamp(2.8rem,8vw,6.5rem)] font-extrabold leading-[0.92] tracking-tight text-foreground">
          <span className="block"><AssembleWord text="Jemsheena M." base={0.15} /></span>
          <span className="mt-1 block text-[clamp(1.6rem,4.5vw,3.4rem)] font-bold text-muted-foreground">
            <AssembleWord text={profile.tagline} base={0.5} accent />
          </span>
        </h1>

        <motion.p
          initial={reduce ? { opacity: 1 } : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE, delay: 1.25 }}
          className="mt-8 max-w-xl text-[1.04rem] leading-relaxed text-foreground/80 sm:text-[1.1rem]"
        >
          {profile.summary}
        </motion.p>

        <motion.div
          initial={reduce ? { opacity: 1 } : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE, delay: 1.4 }}
          className="mt-9 flex flex-nowrap items-center gap-2 overflow-x-auto pb-1 sm:flex-wrap sm:gap-3 sm:overflow-visible sm:pb-0"
        >
          <button
            data-testid={TID.heroPrimaryCta}
            onClick={() => scrollToId("work")}
            className="group inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full bg-primary px-3 py-2 font-mono text-[0.68rem] font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5 sm:px-6 sm:py-3 sm:text-sm"
          >
            Explore the work
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </button>
          <button
            data-testid={TID.heroSecondaryCta}
            onClick={() => scrollToId("contact")}
            className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full border border-border bg-secondary/40 px-3 py-2 font-mono text-[0.68rem] text-foreground backdrop-blur-sm transition-colors hover:border-primary/40 hover:text-primary sm:px-6 sm:py-3 sm:text-sm"
          >
            Let&apos;s talk
          </button>
          <a
            href={profile.github}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full border border-border bg-secondary/40 px-3 py-2 font-mono text-[0.68rem] text-foreground backdrop-blur-sm transition-colors hover:border-primary/40 hover:text-primary sm:px-6 sm:py-3 sm:text-sm"
          >
            <Github className="h-4 w-4" />
            GitHub
          </a>
        </motion.div>
      </motion.div>
      {/* Breathing scroll indicator */}
      <motion.button
        onClick={() => scrollToId("about")}
        aria-label="Scroll to explore"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-7 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-2"
      >
        <span className="font-mono text-[0.6rem] uppercase tracking-[0.3em] text-muted-foreground">scroll</span>
        <span className="relative grid h-9 w-5 place-items-start justify-center rounded-full border border-border pt-1.5">
          <motion.span
            className="h-1.5 w-1.5 rounded-full bg-primary"
            animate={reduce ? {} : { y: [0, 10, 0], opacity: [1, 0.3, 1] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          />
        </span>
      </motion.button>
    </section>
  );
}
